/**
 * Product Development Component
 * 
 * Tracks product development objectives and improvements.
 * Shows:
 * - Product releases (objectives) with timeline
 * - Development items with progress
 * - Status indicators and priority levels
 */

import React from 'react';
import { Timeline, Progress, Tag, Tooltip } from 'antd';
import {
  RocketOutlined,
  CheckCircleOutlined,
  SyncOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  CalendarOutlined
} from '@ant-design/icons';
import { BaseReportingComponent, ComponentData, ReportingComponentProps } from './BaseReportingComponent';
import { fetchProductDevelopmentFromLocalBridge } from '../../../services/bridgeSessionDataAdapter';
import { isDriveFlowReportingEnabled } from '../../../services/driveFlowAdapter';
import styles from './ProductDevelopmentComponent.module.css';

interface Release {
  id: string;
  name: string;
  status: 'completed' | 'in_progress' | 'planned' | 'blocked';
  targetDate: string;
  completedDate?: string;
  progress: number;
  items: DevelopmentItem[];
}

interface DevelopmentItem {
  id: string;
  name: string;
  status: 'completed' | 'in_progress' | 'planned' | 'blocked';
  priority: 'high' | 'medium' | 'low';
  progress: number;
}

interface ProductDevelopmentComponentProps extends Omit<ReportingComponentProps, 'componentType'> {
  /** Show compact view */
  compact?: boolean;
}

const getStatusIcon = (status: string) => {
  switch (status) {
    case 'completed':
      return <CheckCircleOutlined style={{ color: '#22c55e' }} />;
    case 'in_progress':
      return <SyncOutlined spin style={{ color: '#818cf8' }} />;
    case 'planned':
      return <ClockCircleOutlined style={{ color: '#fbbf24' }} />;
    case 'blocked':
      return <ExclamationCircleOutlined style={{ color: '#ef4444' }} />;
    default:
      return <ClockCircleOutlined style={{ color: '#6b7280' }} />;
  }
};

const getStatusColor = (status: string): string => {
  switch (status) {
    case 'completed': return '#22c55e';
    case 'in_progress': return '#818cf8';
    case 'planned': return '#fbbf24';
    case 'blocked': return '#ef4444';
    default: return '#6b7280';
  }
};

const getPriorityColor = (priority: string): string => {
  switch (priority) {
    case 'high': return 'red';
    case 'medium': return 'orange';
    case 'low': return 'blue';
    default: return 'default';
  }
};

const formatDate = (dateStr: string): string => {
  try {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  } catch {
    return dateStr;
  }
};

const ReleaseCard: React.FC<{ release: Release; compact?: boolean }> = ({ release, compact }) => {
  const completedItems = release.items.filter(item => item.status === 'completed').length;
  const totalItems = release.items.length;

  return (
    <div className={`${styles.releaseCard} ${compact ? styles.compact : ''}`}>
      <div className={styles.releaseHeader}>
        <div className={styles.releaseTitle}>
          {getStatusIcon(release.status)}
          <span className={styles.releaseName}>{release.name}</span>
          <Tag color={getStatusColor(release.status)}>
            {release.status.replace('_', ' ')}
          </Tag>
        </div>
        <div className={styles.releaseDate}>
          <CalendarOutlined />
          <span>{formatDate(release.targetDate)}</span>
        </div>
      </div>

      <div className={styles.releaseProgress}>
        <div className={styles.progressHeader}>
          <span className={styles.progressLabel}>
            {completedItems} / {totalItems} items
          </span>
          <span className={styles.progressPercent}>{release.progress}%</span>
        </div>
        <Progress
          percent={release.progress}
          strokeColor={getStatusColor(release.status)}
          showInfo={false}
          size="small"
        />
      </div>

      {!compact && release.items.length > 0 && (
        <div className={styles.releaseItems}>
          {release.items.slice(0, 5).map(item => (
            <div key={item.id} className={styles.releaseItem}>
              <span className={styles.itemStatus}>
                {getStatusIcon(item.status)}
              </span>
              <span className={styles.itemName}>{item.name}</span>
              <Tag color={getPriorityColor(item.priority)} className={styles.itemPriority}>
                {item.priority}
              </Tag>
            </div>
          ))}
          {release.items.length > 5 && (
            <div className={styles.moreItems}>
              +{release.items.length - 5} more items
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const ProductDevelopmentComponent: React.FC<ProductDevelopmentComponentProps> = ({
  compact = false,
  ...props
}) => {
  const renderContent = (data: ComponentData, loading: boolean) => {
    const releases: Release[] = data.data.releases ?? [];
    const summary = data.data.summary ?? {
      total_releases: 0,
      completed: 0,
      in_progress: 0,
      planned: 0,
      total_items: 0
    };

    return (
      <div className={`${styles.productDevelopment} ${compact ? styles.compact : ''}`}>
        <div className={styles.header}>
          <h3 className={styles.title}>
            <RocketOutlined /> Product Development
          </h3>
          <span className={styles.artifactName}>{data.artifact_name}</span>
        </div>

        {/* Summary Stats */}
        <div className={styles.summaryRow}>
          <div className={styles.summaryItem}>
            <span className={styles.summaryValue}>{summary.completed}</span>
            <span className={styles.summaryLabel}>Completed</span>
          </div>
          <div className={styles.summaryItem}>
            <span className={styles.summaryValue}>{summary.in_progress}</span>
            <span className={styles.summaryLabel}>In Progress</span>
          </div>
          <div className={styles.summaryItem}>
            <span className={styles.summaryValue}>{summary.planned}</span>
            <span className={styles.summaryLabel}>Planned</span>
          </div>
          <div className={styles.summaryItem}>
            <span className={styles.summaryValue}>{summary.total_items}</span>
            <span className={styles.summaryLabel}>Total Items</span>
          </div>
        </div>

        {/* Releases Timeline */}
        <div className={styles.releasesSection}>
          <h4 className={styles.sectionTitle}>Release Timeline</h4>
          
          {releases.length === 0 ? (
            <div className={styles.emptyState}>No releases defined</div>
          ) : (
            <Timeline
              className={styles.timeline}
              items={releases.map(release => ({
                dot: getStatusIcon(release.status),
                children: <ReleaseCard release={release} compact={compact} />
              }))}
            />
          )}
        </div>
      </div>
    );
  };

  const customDataFetcher = isDriveFlowReportingEnabled()
    ? (id: string) => fetchProductDevelopmentFromLocalBridge(id)
    : undefined;

  return (
    <BaseReportingComponent
      {...props}
      componentType="product_development"
      customDataFetcher={customDataFetcher}
    >
      {renderContent}
    </BaseReportingComponent>
  );
};

export default ProductDevelopmentComponent;
